/**
 * Order notification system for MōMA • Poké admin panel.
 *
 * Provides:
 *  - Web Audio API generated "ding" sound (no external file needed)
 *  - Browser system notifications (desktop + Android when installed as PWA)
 *  - Settings persistence via localStorage (sound on/off, browser notif on/off)
 *  - Detection of new orders across polling cycles
 */

const STORAGE_KEYS = {
  SOUND: 'moma-admin-sound-enabled',
  BROWSER_NOTIF: 'moma-admin-browser-notif-enabled',
  LAST_SEEN_IDS: 'moma-admin-last-seen-order-ids',
};

/* --------------------- SETTINGS --------------------- */

export function isSoundEnabled() {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.SOUND);
    return v === null ? true : v === '1'; // default ON
  } catch (e) { return true; }
}

export function setSoundEnabled(enabled) {
  try { localStorage.setItem(STORAGE_KEYS.SOUND, enabled ? '1' : '0'); } catch (e) {}
}

export function isBrowserNotifEnabled() {
  try {
    const v = localStorage.getItem(STORAGE_KEYS.BROWSER_NOTIF);
    return v === null ? true : v === '1';
  } catch (e) { return true; }
}

export function setBrowserNotifEnabled(enabled) {
  try { localStorage.setItem(STORAGE_KEYS.BROWSER_NOTIF, enabled ? '1' : '0'); } catch (e) {}
}

/* --------------------- SOUND --------------------- */

// Reusable AudioContext (created lazily, requires user gesture on first call)
let audioCtx = null;
function getAudioCtx() {
  if (!audioCtx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (Ctor) audioCtx = new Ctor();
  }
  return audioCtx;
}

/**
 * Play a pleasant two-tone "ding" — universally works across browsers.
 * If browser blocks (no user gesture yet), silently ignored.
 */
export async function playOrderChime({ volume = 0.5 } = {}) {
  if (!isSoundEnabled()) return;
  try {
    const ctx = getAudioCtx();
    if (!ctx) return;
    if (ctx.state === 'suspended') { await ctx.resume(); }

    const now = ctx.currentTime;
    // First tone (higher pitch, short)
    _tone(ctx, now,      880,  0.14, volume);
    // Second tone (lower, resonant)
    _tone(ctx, now + 0.18, 660, 0.30, volume);
    // Third accent (rising)
    _tone(ctx, now + 0.55, 990, 0.25, volume * 0.8);
  } catch (e) {
    // ignore — likely no user gesture yet
  }
}

function _tone(ctx, when, freq, duration, gain) {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, when);
  // Envelope: attack -> sustain -> release
  g.gain.setValueAtTime(0, when);
  g.gain.linearRampToValueAtTime(gain, when + 0.02);
  g.gain.exponentialRampToValueAtTime(0.001, when + duration);
  osc.connect(g); g.connect(ctx.destination);
  osc.start(when);
  osc.stop(when + duration + 0.02);
}

/* --------------------- BROWSER NOTIFICATIONS --------------------- */

export function browserNotifSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function browserNotifPermission() {
  if (!browserNotifSupported()) return 'unsupported';
  return Notification.permission;
}

export async function requestBrowserNotifPermission() {
  if (!browserNotifSupported()) return 'unsupported';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const p = await Notification.requestPermission();
    return p;
  } catch (e) {
    return 'error';
  }
}

export function showOrderNotification(order) {
  if (!isBrowserNotifEnabled()) return;
  if (!browserNotifSupported()) return;
  if (Notification.permission !== 'granted') return;
  try {
    const title = `🛒 Nuovo ordine #${order.order_number}`;
    const isTakeaway = order.order_type === 'takeaway';
    const body = `${order.customer_name || 'Cliente'} • €${(order.total || 0).toFixed(2)} • ${isTakeaway ? 'Ritiro in negozio' : (order.zone_name || 'Consegna')}${order.scheduled_for ? ' • PRE-ORDINE' : ''}`;
    // Prefer service-worker notification when available (works when tab is not focused)
    if ('serviceWorker' in navigator && navigator.serviceWorker.ready) {
      navigator.serviceWorker.ready.then((reg) => {
        reg.showNotification(title, {
          body,
          icon: '/icon-192.png',
          badge: '/icon-192.png',
          tag: `order-${order.id}`,
          renotify: true,
          requireInteraction: true,
          data: { orderId: order.id, url: '/admin/orders' },
        }).catch(() => {
          try { new Notification(title, { body, icon: '/icon-192.png' }); } catch (e) {}
        });
      });
    } else {
      try { new Notification(title, { body, icon: '/icon-192.png' }); } catch (e) {}
    }
  } catch (e) {
    // ignore
  }
}

/* --------------------- NEW ORDER DETECTION --------------------- */

/**
 * Given the current list of orders (from polling), returns the ones that are
 * genuinely NEW compared to what we saw before (using localStorage as memory).
 * On first invocation it returns [] to avoid a burst of notifications for existing orders.
 */
export function detectNewOrders(currentOrders) {
  if (!Array.isArray(currentOrders)) return [];
  let previouslySeen;
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LAST_SEEN_IDS);
    previouslySeen = raw ? new Set(JSON.parse(raw)) : null;
  } catch (e) { previouslySeen = null; }

  const currentIds = currentOrders.map(o => o.id);

  // First run → remember but don't fire
  if (previouslySeen === null) {
    _persistSeen(currentIds);
    return [];
  }

  const newOnes = currentOrders.filter(o => !previouslySeen.has(o.id));
  _persistSeen(currentIds);
  return newOnes;
}

function _persistSeen(ids) {
  try {
    // Keep only the most recent 500 IDs to avoid unbounded growth
    const trimmed = ids.slice(-500);
    localStorage.setItem(STORAGE_KEYS.LAST_SEEN_IDS, JSON.stringify(trimmed));
  } catch (e) {}
}

export function resetSeenOrders() {
  try { localStorage.removeItem(STORAGE_KEYS.LAST_SEEN_IDS); } catch (e) {}
}
